<template>
  <div class="terminal-qr-scanner">
    <v-select
      v-if="!cameraOnly && cameras.length > 1"
      v-model="selectedCamera"
      :items="cameras"
      item-text="label"
      item-value="deviceId"
      label="เลือกกล้องสำหรับ Scan QR"
      :disabled="cameraBusy"
      outlined
      dense
      hide-details
      @change="changeCamera"
    />
    <div :id="readerId"></div>
    <p v-if="!cameraOnly" role="status" class="scanner-status">{{ scanStatus }}</p>
    <details v-if="!cameraOnly" class="scanner-diagnostics">
      <summary>ข้อมูลตรวจสอบกล้อง</summary>
      <div>Scanner: html5-qrcode 2.3.8 / ZXing</div>
      <div>กล้อง: {{ cameraLabel || "ไม่ทราบชื่อ" }}</div>
      <div>Video: {{ videoInfo || "กำลังเปิดกล้อง" }}</div>
      <div>Focus: {{ focusInfo || "browser ไม่รายงาน" }}</div>
      <div>จำนวนครั้งที่ตรวจ QR: {{ decodeAttempts }}</div>
      <div v-if="lastDecodeError">Decode: {{ lastDecodeError }}</div>
      <div>Browser: {{ browserInfo }}</div>
    </details>
  </div>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

// Serialize camera ownership when the parent remounts after a scan.
let cameraQueue = Promise.resolve();
let readerSequence = 0;
const queueCamera = (operation) => {
  const pending = cameraQueue.then(operation);
  cameraQueue = pending.catch(() => {});
  return pending;
};

export default {
  name: "TerminalQrScanner",
  props: {
    cameraOnly: {
      type: Boolean,
      default: false,
    },
    constraints: {
      type: Object,
      default: () => ({ video: { facingMode: { ideal: "environment" } } }),
    },
  },
  data() {
    return {
      readerId: `terminal-qr-reader-${++readerSequence}`,
      cameras: [],
      selectedCamera: null,
      cameraBusy: true,
      cameraLabel: "",
      videoInfo: "",
      focusInfo: "",
      scanStatus: "กำลังเปิดกล้อง…",
      decodeAttempts: 0,
      lastDecodeError: "",
      browserInfo: navigator.userAgent,
    };
  },
  mounted() {
    this.scannerDisposed = false;
    this.$emit("init", queueCamera(() => this.startScanner()));
  },
  beforeDestroy() {
    this.scannerDisposed = true;
    queueCamera(() => this.stopScanner()).catch((error) => {
      console.error("Unable to stop Terminal QR camera", error);
    });
  },
  methods: {
    async startScanner() {
      if (this.scannerDisposed) return;
      this.cameraBusy = true;
      this.decodeAttempts = 0;
      this.decodeCount = 0;
      this.lastDiagnosticAt = 0;
      this.lastDecodeError = "";
      this.scanStatus = "กำลังเปิดกล้อง…";
      this.scanner = new Html5Qrcode(this.readerId, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        useBarCodeDetectorIfSupported: false,
        verbose: false,
      });
      const onDecoded = (value) => {
        if (!this.scannerDisposed && value) {
          this.scanStatus = "อ่าน QR สำเร็จ";
          this.$emit("decode", value);
        }
      };
      const onDecodeError = (message) => {
        if (this.scannerDisposed) return;
        this.decodeCount++;
        const now = Date.now();
        if (now - this.lastDiagnosticAt < 1000) return;
        this.lastDiagnosticAt = now;
        this.decodeAttempts = this.decodeCount;
        this.lastDecodeError = String(message).slice(0, 250);
        this.scanStatus = "กล้องเปิดแล้ว แต่ยังอ่าน QR ไม่ได้ ให้ QR อยู่ครบในภาพและลองเปลี่ยนระยะ";
      };
      const camera = this.selectedCamera || { facingMode: "environment" };
      const videoConstraints = { ...this.constraints.video };
      if (this.selectedCamera) {
        delete videoConstraints.facingMode;
        videoConstraints.deviceId = { exact: this.selectedCamera };
      }
      try {
        await this.scanner.start(
          camera,
          { fps: 5, videoConstraints },
          onDecoded,
          onDecodeError
        );
      } catch (error) {
        if (this.scannerDisposed) return;
        // Retry only unsupported constraints; permission errors need user action.
        if (!/OverconstrainedError|ConstraintNotSatisfiedError/.test(
          `${error?.name || ""} ${error?.message || String(error)}`
        )) throw error;
        await this.scanner.start(
          camera,
          { fps: 5, videoConstraints: this.selectedCamera
            ? { deviceId: { exact: this.selectedCamera } }
            : { facingMode: { ideal: "environment" } } },
          onDecoded,
          onDecodeError
        );
      }
      if (this.scannerDisposed) return;
      let capabilities = {};
      try {
        capabilities = this.scanner.getRunningTrackCapabilities();
      } catch (error) {
        // Some browsers stream video without exposing camera capabilities.
      }
      if (capabilities.focusMode?.includes("continuous")) {
        try {
          await this.scanner.applyVideoConstraints({
            advanced: [{ focusMode: "continuous" }],
          });
        } catch (error) {
          // Optional autofocus must not prevent decoding on unsupported devices.
          console.warn("Continuous QR autofocus unavailable", error);
        }
      }
      if (this.scannerDisposed) return;
      const settings = this.scanner.getRunningTrackSettings();
      this.videoInfo = `${settings.width || "?"} × ${settings.height || "?"}, ${settings.frameRate || "?"} FPS`;
      this.focusInfo = settings.focusMode || (capabilities.focusMode || []).join(", ");
      try {
        this.cameras = (await navigator.mediaDevices.enumerateDevices())
          .filter((device) => device.kind === "videoinput")
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: device.label || `กล้อง ${index + 1}`,
          }));
        this.selectedCamera = settings.deviceId || this.selectedCamera;
        this.cameraLabel = this.cameras.find((device) => device.deviceId === this.selectedCamera)?.label || "";
      } catch (error) {
        // Camera enumeration is optional; keep the running stream.
      }
      this.cameraBusy = false;
      return { capabilities };
    },
    changeCamera() {
      this.cameraBusy = true;
      this.$emit("init", queueCamera(async () => {
        await this.stopScanner();
        return this.startScanner();
      }));
    },
    async stopScanner() {
      if (!this.scanner) return;
      if (this.scanner.isScanning) await this.scanner.stop();
      this.scanner.clear();
      this.scanner = null;
    },
  },
};
</script>

<style scoped>
.terminal-qr-scanner {
  width: 100%;
}
.scanner-status,
.scanner-diagnostics {
  margin-top: 12px;
  color: #231f20;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.scanner-diagnostics summary {
  cursor: pointer;
}
</style>
